import { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import toast from 'react-hot-toast';
import {
  PlusIcon, XMarkIcon, CheckCircleIcon, LockOpenIcon,
  PencilSquareIcon, TrashIcon, ChevronRightIcon, PaperClipIcon,
} from '@heroicons/react/24/outline';
import { TermSettingsEditor } from '../components/TermSettings';

const style = `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');
  .ta2 * { box-sizing:border-box; }
  .ta2 { font-family:'Sora',sans-serif; color:#1a1d2e; animation:ta2Fade .4s ease both; }
  @keyframes ta2Fade { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:translateY(0)} }
  .ta2-header { display:flex; align-items:flex-start; justify-content:space-between; margin-bottom:1.5rem; flex-wrap:wrap; gap:1rem; }
  .ta2-title  { font-size:1.35rem; font-weight:700; letter-spacing:-.02em; color:#0f1123; }
  .ta2-sub    { font-size:.76rem; color:#9094a8; margin-top:3px; }
  .btn { display:inline-flex; align-items:center; gap:7px; padding:9px 16px; border:none; border-radius:10px; font-family:'Sora',sans-serif; font-size:.8rem; font-weight:600; cursor:pointer; transition:all .18s; }
  .btn svg { width:15px; height:15px; stroke-width:2; }
  .btn-blue  { background:#3b6ef8; color:white; }
  .btn-blue:hover  { background:#2d5ef0; box-shadow:0 4px 14px rgba(59,110,248,.35); transform:translateY(-1px); }
  .btn-green { background:#10c98e; color:white; }
  .btn-green:hover { background:#0db87e; }
  .btn-ghost { background:#f0f2f7; color:#6b7194; }
  .btn-ghost:hover { background:#e4e7f0; }
  .btn-red   { background:#fef2f2; color:#ef4444; border:1px solid #fecaca; }
  .btn-red:hover { background:#ef4444; color:white; }
  .btn-amber { background:#fffbeb; color:#f59e0b; border:1px solid #fde68a; }
  .btn-amber:hover { background:#f59e0b; color:white; }
  .btn-sm { padding:6px 11px; font-size:.74rem; }
  .btn:disabled { opacity:.5; cursor:not-allowed; transform:none !important; }
  .breadcrumb { display:flex; align-items:center; gap:6px; margin-bottom:1.2rem; flex-wrap:wrap; }
  .bc-item { font-size:.78rem; font-weight:600; color:#9094a8; cursor:pointer; transition:color .15s; }
  .bc-item:hover { color:#3b6ef8; }
  .bc-item.active { color:#0f1123; cursor:default; }
  .bc-sep { color:#d1d5db; font-size:.82rem; }
  .card-grid { display:grid; grid-template-columns:repeat(auto-fill,minmax(200px,1fr)); gap:1rem; margin-bottom:1rem; }
  .grade-card { background:white; border-radius:14px; border:1px solid #e8ebf4; padding:1.1rem 1.2rem; cursor:pointer; transition:all .15s; }
  .grade-card:hover { border-color:#3b6ef8; box-shadow:0 4px 16px rgba(59,110,248,.1); transform:translateY(-1px); }
  .gc-name  { font-size:.95rem; font-weight:700; color:#0f1123; margin-bottom:3px; }
  .gc-sub   { font-size:.72rem; color:#9094a8; }
  .gc-arrow { display:flex; align-items:center; justify-content:space-between; margin-top:10px; }
  .gc-arrow svg { width:15px; height:15px; stroke:#3b6ef8; stroke-width:2; }
  .subj-card { background:white; border-radius:14px; border:1px solid #e8ebf4; padding:1rem 1.2rem; cursor:pointer; transition:all .15s; }
  .subj-card:hover { border-color:#8b5cf6; box-shadow:0 4px 16px rgba(139,92,246,.12); transform:translateY(-1px); }
  .sec-tabs { display:flex; gap:6px; flex-wrap:wrap; margin-bottom:1.2rem; }
  .sec-tab { padding:6px 16px; border-radius:99px; border:1.5px solid #e0e4f0; font-family:'Sora',sans-serif; font-size:.76rem; font-weight:600; cursor:pointer; background:white; color:#6b7194; transition:all .15s; }
  .sec-tab.active { background:#3b6ef8; color:white; border-color:#3b6ef8; }
  .panel { background:white; border-radius:16px; border:1px solid #e8ebf4; overflow:hidden; margin-bottom:1rem; }
  .panel-head { display:flex; align-items:center; justify-content:space-between; padding:14px 18px; border-bottom:1px solid #f0f2f8; flex-wrap:wrap; gap:8px; }
  .panel-title { font-size:.86rem; font-weight:600; color:#1a1d2e; }
  .row-item { display:flex; align-items:center; justify-content:space-between; padding:13px 18px; border-bottom:1px solid #f5f6fb; gap:10px; flex-wrap:wrap; cursor:pointer; transition:background .12s; }
  .row-item:last-child { border-bottom:none; }
  .row-item:hover { background:#fafbff; }
  .row-title { font-size:.83rem; font-weight:600; color:#1a1d2e; }
  .row-sub   { font-size:.72rem; color:#9094a8; margin-top:2px; }
  .row-desc  { font-size:.72rem; color:#6b7194; margin-top:3px; font-style:italic; }
  .row-actions { display:flex; gap:6px; align-items:center; flex-shrink:0; flex-wrap:wrap; }
  .status-badge { display:inline-flex; font-size:.67rem; font-weight:600; padding:3px 9px; border-radius:99px; white-space:nowrap; }
  .status-badge.draft            { background:#f3f4f6; color:#6b7280; }
  .status-badge.submitted        { background:#ecfdf5; color:#10c98e; }
  .status-badge.locked           { background:#eef2ff; color:#3b6ef8; }
  .status-badge.unlock_requested { background:#fffbeb; color:#f59e0b; }
  .status-badge.unlocked         { background:#fdf4ff; color:#a855f7; }
  .clo-chip { display:inline-flex; align-items:center; gap:3px; font-size:.65rem; font-weight:700; padding:2px 7px; border-radius:6px; background:#eef2ff; color:#3b6ef8; font-family:'JetBrains Mono',monospace; white-space:nowrap; }
  .upload-zone { border:2px dashed #d0d5e8; border-radius:10px; padding:14px; text-align:center; cursor:pointer; transition:all .15s; background:#fafbfc; }
  .upload-zone:hover, .upload-zone.drag { border-color:#3b6ef8; background:#f0f4ff; }
  .upload-zone input { display:none; }
  .uz-icon { font-size:1.4rem; margin-bottom:4px; }
  .uz-text { font-size:.78rem; font-weight:600; color:#1a1d2e; }
  .uz-sub  { font-size:.7rem; color:#9094a8; margin-top:2px; }
  .file-chip { display:inline-flex; align-items:center; gap:6px; padding:5px 10px; background:#eef2ff; border-radius:8px; font-size:.74rem; color:#3b6ef8; font-weight:600; margin-top:6px; }
  .file-chip button { background:none; border:none; cursor:pointer; color:#9094a8; display:flex; align-items:center; padding:0; }
  .file-chip button:hover { color:#ef4444; }
  .file-chip button svg { width:13px; height:13px; stroke-width:2.5; }
  .marks-header { display:flex; align-items:flex-start; justify-content:space-between; margin-bottom:1.5rem; flex-wrap:wrap; gap:1rem; }
  .marks-actions { display:flex; gap:8px; flex-wrap:wrap; align-items:center; }
  .marks-table { width:100%; border-collapse:collapse; }
  .marks-table thead { background:#fafbff; border-bottom:2px solid #edf0f9; }
  .marks-table th { padding:10px 12px; text-align:center !important; font-size:.65rem; font-weight:700; color:#9094a8; text-transform:uppercase; letter-spacing:.07em; white-space:nowrap; }
  .marks-table tbody tr { border-bottom:1px solid #f3f5fb; }
  .marks-table tbody tr:last-child { border-bottom:none; }
  .marks-table td { padding:8px 12px; font-size:.8rem; vertical-align:middle; text-align:center !important; }
  .marks-input { width:80px; font-family:'JetBrains Mono',monospace; font-size:.82rem; padding:6px 8px; border:1.5px solid #e0e4f0; border-radius:7px; background:#f8f9fc; text-align:center; outline:none; }
  .marks-input:focus { border-color:#3b6ef8; background:white; }
  .marks-input:disabled { background:#f3f4f6; color:#b0b5cc; }
  .chk { width:16px; height:16px; cursor:pointer; }
  .student-name { font-weight:600; font-size:.8rem; color:#0f1123; }
  .student-roll { font-size:.7rem; color:#9094a8; margin-top:1px; font-family:'JetBrains Mono',monospace; }
  .marks-cards { display:none; padding:10px; }
  .marks-card { background:#fafbff; border-radius:12px; border:1px solid #e8ebf4; padding:13px; margin-bottom:8px; }
  .mc-name { font-weight:700; font-size:.84rem; color:#0f1123; margin-bottom:2px; }
  .mc-roll { font-size:.7rem; color:#9094a8; font-family:'JetBrains Mono',monospace; margin-bottom:8px; }
  .mc-row  { display:flex; align-items:center; justify-content:space-between; font-size:.76rem; padding:4px 0; border-bottom:1px solid #f0f2f8; gap:8px; }
  .mc-row:last-child { border-bottom:none; }
  .mc-label { color:#9094a8; font-weight:600; white-space:nowrap; }
  .mc-checks { display:flex; gap:16px; }
  .mc-check-item { display:flex; align-items:center; gap:5px; font-size:.76rem; color:#6b7194; }
  .form-grid { display:grid; grid-template-columns:1fr 1fr; gap:14px; }
  .form-grid > *, .fg > * { min-width:0; }
  .form-field { display:flex; flex-direction:column; gap:5px; }
  .form-field.span2 { grid-column:1/-1; }
  .form-label { font-size:.68rem; font-weight:700; color:#9094a8; text-transform:uppercase; letter-spacing:.06em; }
  .form-label .req { color:#ef4444; margin-left:2px; }
  .form-input, .form-select { font-family:'Sora',sans-serif; font-size:.82rem; padding:9px 11px; border:1.5px solid #e0e4f0; border-radius:9px; background:#f8f9fc; color:#1a1d2e; outline:none; width:100%; }
  .form-input:focus, .form-select:focus { border-color:#3b6ef8; background:white; }
  .form-textarea { font-family:'Sora',sans-serif; font-size:.82rem; padding:9px 11px; border:1.5px solid #e0e4f0; border-radius:9px; background:#f8f9fc; color:#1a1d2e; outline:none; width:100%; resize:vertical; min-height:70px; }
  .form-textarea:focus { border-color:#3b6ef8; background:white; }
  .form-btns { display:flex; gap:8px; margin-top:8px; flex-wrap:wrap; }
  .ta2-empty   { text-align:center; padding:3rem 2rem; color:#9094a8; font-size:.82rem; }
  .ta2-loading { display:flex; align-items:center; justify-content:center; height:200px; gap:10px; color:#9094a8; }
  .spinner { width:18px; height:18px; border:2px solid #e4e7f0; border-top-color:#10c98e; border-radius:50%; animation:spin .7s linear infinite; }
  @keyframes spin { to{transform:rotate(360deg);} }
  @media (max-width:768px) {
    .ta2-title { font-size:1.1rem; }
    .card-grid { grid-template-columns:1fr 1fr; }
    .marks-table-wrap { display:none !important; }
    .marks-cards { display:block; }
    .marks-header { flex-direction:column; gap:10px; }
    .marks-actions .btn { flex:1; justify-content:center; }
    .form-grid { grid-template-columns:1fr; }
    .form-field.span2 { grid-column:1; }
    .form-btns .btn { flex:1; justify-content:center; }
  }
`;

const fmtDate = (d) => {
  if (!d) return '—';
  const dt = new Date(d);
  return `${String(dt.getDate()).padStart(2,'0')}-${dt.toLocaleDateString('en-US',{month:'short'})}-${dt.getFullYear()}`;
};
const curYear = () => { const y = new Date().getFullYear(); return `${y}-${y+1}`; };
const SUBJ_COLORS = ['#3b6ef8','#10c98e','#f59e0b','#8b5cf6','#ef4444','#06b6d4','#84cc16','#f97316'];

const TeacherAssessments = () => {
  const [navStep,       setNavStep]       = useState('grades');
  const [activeClass,   setActiveClass]   = useState(null);
  const [activeSection, setActiveSection] = useState('');
  const [activeSubject, setActiveSubject] = useState('');
  const [classes,       setClasses]       = useState([]);
  const [assessTypes,   setAssessTypes]   = useState([]);
  const [assessments,   setAssessments]   = useState([]);
  const [selected,      setSelected]      = useState(null);
  const [marksData,     setMarksData]     = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [teacherSubjects, setTeacherSubjects] = useState([]);
  const [saving,        setSaving]        = useState(false);
  const [unlockReason,  setUnlockReason]  = useState('');
  const [obeEnabled,    setObeEnabled]    = useState(false);
  const [assessmentMode, setAssessmentMode] = useState('assessment');
  const [termPeriods,    setTermPeriods]    = useState([]);
  const isTermMode = assessmentMode === 'term';

  const [form, setForm] = useState({
    assessmentTypeId: '', termPeriodId: '', academicYear: curYear(),
    maxMarks: '', description: '', date: '', countsTowardTerm: true,
  });
  const [uploadFile, setUploadFile] = useState(null);
  const [dragOver,   setDragOver]   = useState(false);

  useEffect(() => {
    const ctrl = new AbortController();
    api.get('/teacher/assessment-mode', { signal: ctrl.signal })
      .then(({ data }) => data.assessmentMode || 'assessment')
      .catch(() => 'assessment')
      .then(mode => {
        setAssessmentMode(mode);
        const kindParam = mode === 'term' ? '?kind=term_component' : '';
        return Promise.all([
          api.get('/teacher/classes',                      { signal: ctrl.signal }),
          api.get(`/teacher/assessment-types${kindParam}`,  { signal: ctrl.signal }),
          api.get('/teacher/profile',                       { signal: ctrl.signal }),
          api.get('/teacher/obe-settings',                  { signal: ctrl.signal }).catch(() => ({ data: {} })),
          api.get('/teacher/term-periods',                  { signal: ctrl.signal }).catch(() => ({ data: [] })),
        ]);
      }).then(([c, t, p, obeRes, termRes]) => {
        setClasses(c.data.classes || []);
        setAssessTypes(t.data);
        setTeacherSubjects(p.data.subjects || []);
        if (obeRes?.data?.obeEnabled) setObeEnabled(true);
        setTermPeriods(termRes?.data || []);
      }).catch(err => {
        if (err.code !== 'ERR_CANCELED') toast.error('Failed to load');
      }).finally(() => setLoading(false));
    return () => ctrl.abort();
  }, []);

  const fetchAssessments = useCallback(async (classId, section, subject) => {
    try {
      setLoading(true);
      const { data } = await api.get('/teacher/assessments', {
        params: { classId, section, subject },
      });
      setAssessments(data);
    } catch { toast.error('Failed to load assessments'); }
    finally  { setLoading(false); }
  }, []);

  const getSubjects = () => teacherSubjects;

  const goToGrades   = () => { setNavStep('grades'); setActiveClass(null); setActiveSection(''); setActiveSubject(''); };
  const goToSections = (cls) => { setActiveClass(cls); setActiveSection(''); setNavStep('sections'); };
  const goToSubjects = (sec) => { setActiveSection(sec); setNavStep('subjects'); };
  const goToList     = (subject) => { setActiveSubject(subject); setNavStep('list'); fetchAssessments(activeClass._id, activeSection, subject); };

  const handleCreate = async () => {
    if (!form.assessmentTypeId) { toast.error('Select an assessment type'); return; }
    if (!form.maxMarks)         { toast.error('Enter max marks'); return; }
    if (!form.description.trim()) { toast.error('Description is required'); return; }
    if (!form.date)             { toast.error('Select a date'); return; }
    try {
      setSaving(true);
      const fd = new FormData();
      fd.append('classId',          activeClass._id);
      fd.append('section',          activeSection);
      fd.append('subject',          activeSubject);
      fd.append('assessmentTypeId', form.assessmentTypeId);
      if (isTermMode) {
        if (form.termPeriodId) fd.append('termPeriodId', form.termPeriodId);
        fd.append('countsTowardTerm', form.countsTowardTerm);
      }
      fd.append('academicYear',     form.academicYear);
      fd.append('maxMarks',         form.maxMarks);
      fd.append('description',      form.description.trim());
      fd.append('date',             form.date);
      if (uploadFile) fd.append('file', uploadFile);
      const { data } = await api.post('/teacher/assessments', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Assessment created');
      if (uploadFile) toast.success('File uploaded — students can see it in Materials');
      setForm({ assessmentTypeId:'', termPeriodId:'', academicYear:curYear(), maxMarks:'', description:'', date:'', countsTowardTerm:true });
      setUploadFile(null);
      await openMarks(data);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to create');
    } finally { setSaving(false); }
  };

  const openMarks = async (assessment) => {
    try {
      const { data } = await api.get(`/teacher/assessments/${assessment._id}`);
      setSelected(data);
      setMarksData(data.marks.map(m => ({
        studentId:     m.student?._id || m.student,
        name:          m.student?.user?.name || '—',
        rollNo:        m.student?.rollNo,
        admissionNo:   m.student?.admissionNo,
        marksObtained: m.marksObtained ?? '',
        isAbsent:      m.isAbsent  || false,
        isNA:          m.isNA      || false,
        remarks:       m.remarks   || '',
      })));
      setNavStep('marks');
    } catch { toast.error('Failed to load assessment'); }
  };

  const updateMark = (idx, field, value) => {
    setMarksData(prev => {
      const next = [...prev];
      next[idx] = { ...next[idx], [field]: value };
      if (field === 'isAbsent' && value) { next[idx].marksObtained = ''; next[idx].isNA = false; }
      if (field === 'isNA'     && value) { next[idx].marksObtained = ''; next[idx].isAbsent = false; }
      return next;
    });
  };

  const handleSaveMarks = async (andSubmit = false) => {
    try {
      setSaving(true);
      const marks = marksData.map(m => ({
        studentId:     m.studentId,
        marksObtained: m.isAbsent || m.isNA ? null : (m.marksObtained === '' ? null : Number(m.marksObtained)),
        isAbsent:      m.isAbsent,
        isNA:          m.isNA,
        remarks:       m.remarks,
      }));
      await api.put(`/teacher/assessments/${selected._id}/marks`, { marks });
      if (andSubmit) {
        await api.put(`/teacher/assessments/${selected._id}/submit`);
        toast.success('Marks saved and submitted ✅');
      } else {
        toast.success('Marks saved as draft');
      }
      fetchAssessments(activeClass._id, activeSection, activeSubject);
      setNavStep('list');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed');
    } finally { setSaving(false); }
  };

  const handleDelete = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm('Delete this draft assessment?')) return;
    try {
      await api.delete(`/teacher/assessments/${id}`);
      toast.success('Deleted');
      setAssessments(prev => prev.filter(a => a._id !== id));
    } catch (err) {
      toast.error(err.response?.data?.message || 'Only drafts can be deleted');
    }
  };

  const handleRequestUnlock = async () => {
    if (!unlockReason.trim()) { toast.error('Please provide a reason'); return; }
    try {
      setSaving(true);
      await api.put(`/teacher/assessments/${selected._id}/request-unlock`, { reason: unlockReason });
      toast.success('Unlock request sent');
      setUnlockReason('');
      fetchAssessments(activeClass._id, activeSection, activeSubject);
      setNavStep('list');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed');
    } finally { setSaving(false); }
  };

  const Breadcrumb = () => (
    <div className="breadcrumb">
      <span className="bc-item" onClick={goToGrades}>Assessments</span>
      {activeClass && <>
        <span className="bc-sep">›</span>
        <span className={`bc-item ${navStep==='sections'?'active':''}`}
          onClick={() => { setNavStep('sections'); setActiveSection(''); setActiveSubject(''); }}>
          {activeClass.name}
        </span>
      </>}
      {activeSection && <>
        <span className="bc-sep">›</span>
        <span className={`bc-item ${navStep==='subjects'&&!activeSubject?'active':''}`}
          onClick={() => { setNavStep('subjects'); setActiveSubject(''); }}>
          Section {activeSection}
        </span>
      </>}
      {activeSubject && <>
        <span className="bc-sep">›</span>
        <span className={`bc-item ${navStep==='list'?'active':''}`} onClick={() => setNavStep('list')}>
          {activeSubject}
        </span>
      </>}
      {navStep === 'marks'  && <><span className="bc-sep">›</span><span className="bc-item active">Marks</span></>}
      {navStep === 'create' && <><span className="bc-sep">›</span><span className="bc-item active">Create Assessment</span></>}
    </div>
  );

  // ── MARKS VIEW ───────────────────────────────────────────────────────────────
  if (navStep === 'marks' && selected) {
    const canEdit = ['draft', 'unlocked'].includes(selected.status);
    return (
      <div className="ta2"><style>{style}</style>
        <Breadcrumb />
        <div className="marks-header">
          <div>
            <div className="ta2-title">{selected.assessmentType?.name} — {selected.subject}</div>
            <div className="ta2-sub">
              {selected.class?.name} · Sec {selected.section} · Max: {selected.maxMarks} · {fmtDate(selected.date)}
            </div>
            {selected.description && (
              <div style={{fontSize:'.76rem',color:'#6b7194',marginTop:4,fontStyle:'italic'}}>
                📝 {selected.description}
              </div>
            )}
            {isTermMode && (
              <TermSettingsEditor assessment={selected} terms={termPeriods} role="teacher"
                onSaved={s => setSelected(prev => ({ ...prev, ...s }))} />
            )}
            {/* ── OBE: CLO chips in marks header ── */}
            {obeEnabled && selected.cloMappings?.length > 0 && (
              <div style={{display:'flex',alignItems:'center',gap:5,marginTop:6,flexWrap:'wrap'}}>
                <span style={{fontSize:'.68rem',color:'#9094a8',fontWeight:600}}>CLOs:</span>
                {selected.cloMappings.map((m, i) => (
                  <span key={i} className="clo-chip">
                    {m.clo?.code || 'CLO'}
                    {m.weightage && m.weightage !== 100 ? ` ${m.weightage}%` : ''}
                  </span>
                ))}
              </div>
            )}
            {selected.fileUrl && (
              <a href={selected.fileUrl} target="_blank" rel="noreferrer"
                style={{display:'inline-flex',alignItems:'center',gap:5,marginTop:6,fontSize:'.74rem',color:'#3b6ef8',fontWeight:600,textDecoration:'none'}}>
                <PaperClipIcon style={{width:13,height:13,strokeWidth:2.5}} />
                View Attached File
              </a>
            )}
          </div>
          <div className="marks-actions">
            <span className={`status-badge ${selected.status}`}>{selected.status.replace('_',' ')}</span>
            {canEdit && <>
              <button className="btn btn-ghost" onClick={() => handleSaveMarks(false)} disabled={saving}>Save Draft</button>
              <button className="btn btn-green" onClick={() => handleSaveMarks(true)} disabled={saving}>
                <CheckCircleIcon />{saving ? 'Saving…' : 'Save & Submit'}
              </button>
            </>}
            {['submitted','locked'].includes(selected.status) && (
              <button className="btn btn-amber" onClick={() => setNavStep('unlock')}>
                <LockOpenIcon />Request Unlock
              </button>
            )}
          </div>
        </div>

        {!canEdit && (
          <div style={{background:'#eef2ff',border:'1px solid #c7d2fe',borderRadius:12,padding:'10px 16px',marginBottom:14,fontSize:'.78rem',color:'#3730a3'}}>
            🔒 <strong>{selected.status.replace('_',' ')}</strong>
            {selected.status === 'unlock_requested' && ' — Unlock request pending admin approval.'}
            {['submitted','locked'].includes(selected.status) && ' — Request unlock to edit marks.'}
          </div>
        )}

        <div className="panel">
          <div className="marks-table-wrap" style={{overflowX:'auto'}}>
            <table className="marks-table">
              <thead>
                <tr>
                  <th>#</th><th>Student</th>
                  <th>Marks / {selected.maxMarks}</th>
                  <th>Absent</th><th>N/A</th><th>Remarks</th>
                </tr>
              </thead>
              <tbody>
                {marksData.map((m, i) => (
                  <tr key={m.studentId}>
                    <td style={{color:'#9094a8',fontFamily:"'JetBrains Mono',monospace",fontSize:'.74rem'}}>{i+1}</td>
                    <td>
                      <div className="student-name">{m.name}</div>
                      <div className="student-roll">Roll: {m.rollNo} · {m.admissionNo}</div>
                    </td>
                    <td>
                      <input type="number" className="marks-input"
                        value={m.marksObtained} min={0} max={selected.maxMarks}
                        disabled={!canEdit || m.isAbsent || m.isNA}
                        onChange={e => updateMark(i, 'marksObtained', e.target.value)} />
                    </td>
                    <td><input type="checkbox" className="chk" checked={m.isAbsent} disabled={!canEdit} onChange={e => updateMark(i,'isAbsent',e.target.checked)} /></td>
                    <td><input type="checkbox" className="chk" checked={m.isNA}     disabled={!canEdit} onChange={e => updateMark(i,'isNA',e.target.checked)} /></td>
                    <td>
                      <input type="text" className="marks-input" style={{width:120}}
                        value={m.remarks} disabled={!canEdit} placeholder="remarks…"
                        onChange={e => updateMark(i,'remarks',e.target.value)} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="marks-cards">
            {marksData.map((m, i) => (
              <div key={m.studentId} className="marks-card">
                <div className="mc-name">{m.name}</div>
                <div className="mc-roll">Roll: {m.rollNo} · {m.admissionNo}</div>
                <div className="mc-row">
                  <span className="mc-label">Marks / {selected.maxMarks}</span>
                  <input type="number" className="marks-input" value={m.marksObtained}
                    min={0} max={selected.maxMarks}
                    disabled={!canEdit || m.isAbsent || m.isNA}
                    onChange={e => updateMark(i,'marksObtained',e.target.value)} />
                </div>
                <div className="mc-row">
                  <span className="mc-label">Status</span>
                  <div className="mc-checks">
                    <label className="mc-check-item"><input type="checkbox" checked={m.isAbsent} disabled={!canEdit} onChange={e => updateMark(i,'isAbsent',e.target.checked)} /> Absent</label>
                    <label className="mc-check-item"><input type="checkbox" checked={m.isNA}     disabled={!canEdit} onChange={e => updateMark(i,'isNA',e.target.checked)} /> N/A</label>
                  </div>
                </div>
                <div className="mc-row">
                  <span className="mc-label">Remarks</span>
                  <input type="text" className="marks-input" style={{width:130}}
                    value={m.remarks} disabled={!canEdit} placeholder="remarks…"
                    onChange={e => updateMark(i,'remarks',e.target.value)} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // ── UNLOCK VIEW ──────────────────────────────────────────────────────────────
  if (navStep === 'unlock' && selected) return (
    <div className="ta2"><style>{style}</style>
      <Breadcrumb />
      <div className="panel" style={{maxWidth:500}}>
        <div className="panel-head">
          <div className="panel-title">Request Unlock — {selected.assessmentType?.name}</div>
        </div>
        <div style={{padding:'18px 20px'}}>
          <div style={{fontSize:'.8rem',color:'#6b7194',marginBottom:16,lineHeight:1.6}}>
            Explain why you need to edit this submitted assessment. Admin will review your request.
          </div>
          <div className="form-field">
            <label className="form-label">Reason <span className="req">*</span></label>
            <textarea className="form-textarea" required value={unlockReason}
              onChange={e => setUnlockReason(e.target.value)}
              placeholder="e.g. Entered wrong marks for roll no. 5…"
              style={{minHeight:90}} />
          </div>
          <div className="form-btns">
            <button className="btn btn-ghost" onClick={() => setNavStep('marks')}>Cancel</button>
            <button className="btn btn-amber" onClick={handleRequestUnlock} disabled={saving}>
              <LockOpenIcon />{saving ? 'Sending…' : 'Send Request'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  // ── CREATE VIEW ──────────────────────────────────────────────────────────────
  if (navStep === 'create') return (
    <div className="ta2"><style>{style}</style>
      <Breadcrumb />
      <div className="panel" style={{maxWidth:620}}>
        <div className="panel-head">
          <div className="panel-title">New Assessment — {activeSubject} · {activeClass?.name} · Sec {activeSection}</div>
        </div>
        <div style={{padding:'18px 20px'}}>
          <div className="form-grid">
            <div className="form-field">
              <label className="form-label">{isTermMode ? 'Component' : 'Assessment Type'} <span className="req">*</span></label>
              <select className="form-select" value={form.assessmentTypeId}
                onChange={e => setForm({...form, assessmentTypeId:e.target.value})}>
                <option value="">Select {isTermMode ? 'component' : 'type'}…</option>
                {assessTypes.filter(t=>t.active).map(t => <option key={t._id} value={t._id}>{t.name}</option>)}
              </select>
            </div>
            {isTermMode && (
              <div className="form-field">
                <label className="form-label">Term Period</label>
                <select className="form-select" value={form.termPeriodId}
                  onChange={e => setForm({...form, termPeriodId:e.target.value})}>
                  <option value="">No term (standalone entry)</option>
                  {termPeriods.filter(t=>t.active).map(t => (
                    <option key={t._id} value={t._id}>{t.name}</option>
                  ))}
                </select>
                <div className="form-hint">Leave as "No term" if this assessment shouldn't count toward any term's report.</div>
              </div>
            )}
            {isTermMode && form.termPeriodId && (
              <div className="form-field span2">
                <label style={{display:'flex',alignItems:'center',gap:8,cursor:'pointer'}}>
                  <input type="checkbox" className="chk" checked={form.countsTowardTerm}
                    onChange={e => setForm({...form, countsTowardTerm:e.target.checked})} />
                  <span style={{fontSize:'.8rem',fontWeight:600,color:'#1a1d2e'}}>Count this toward the term's %</span>
                </label>
                <div style={{fontSize:'.7rem',color:'#9094a8',marginTop:3}}>
                  Uncheck for extra/practice entries you want recorded but not scored into the term total.
                </div>
              </div>
            )}
            <div className="form-field">
              <label className="form-label">Date <span className="req">*</span></label>
              <input type="date" className="form-input" value={form.date}
                onChange={e => setForm({...form, date:e.target.value})} />
            </div>
            <div className="form-field">
              <label className="form-label">Max Marks <span className="req">*</span></label>
              <input type="number" className="form-input" value={form.maxMarks} min={1}
                placeholder="e.g. 20"
                onChange={e => setForm({...form, maxMarks:e.target.value})} />
            </div>
            <div className="form-field">
              <label className="form-label">Academic Year</label>
              <input type="text" className="form-input" value={form.academicYear}
                placeholder="2025-2026"
                onChange={e => setForm({...form, academicYear:e.target.value})} />
            </div>
            <div className="form-field span2">
              <label className="form-label">Description <span className="req">*</span></label>
              <textarea className="form-textarea" value={form.description}
                placeholder="e.g. Chapter 5 — Photosynthesis written test…"
                onChange={e => setForm({...form, description:e.target.value})} />
            </div>
            <div className="form-field span2">
              <label className="form-label">
                Attach Assessment File
                <span style={{fontWeight:400,color:'#9094a8',textTransform:'none',marginLeft:6}}>
                  (optional — students can see it in Materials &amp; practice quiz)
                </span>
              </label>
              {!uploadFile ? (
                <div
                  className={`upload-zone ${dragOver?'drag':''}`}
                  onDragOver={e=>{e.preventDefault();setDragOver(true);}}
                  onDragLeave={()=>setDragOver(false)}
                  onDrop={e=>{e.preventDefault();setDragOver(false);setUploadFile(e.dataTransfer.files[0]);}}
                  onClick={()=>document.getElementById('assess-file-input').click()}>
                  <input id="assess-file-input" type="file"
                    accept=".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.csv,.txt"
                    onChange={e=>setUploadFile(e.target.files[0])} />
                  <div className="uz-icon">📎</div>
                  <div className="uz-text">Drag &amp; drop or click to upload</div>
                  <div className="uz-sub">PDF, DOCX, XLSX, CSV, TXT, PPT</div>
                </div>
              ) : (
                <div className="file-chip">
                  <PaperClipIcon style={{width:13,height:13,strokeWidth:2.5}} />
                  <span>{uploadFile.name}</span>
                  <button onClick={()=>setUploadFile(null)}><XMarkIcon /></button>
                </div>
              )}
            </div>
          </div>
          <div className="form-btns">
            <button className="btn btn-ghost" onClick={()=>setNavStep('list')}>Cancel</button>
            <button className="btn btn-blue" onClick={handleCreate} disabled={saving}>
              {saving ? 'Creating…' : 'Create & Enter Marks'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  // ── ASSESSMENT LIST ──────────────────────────────────────────────────────────
  if (navStep === 'list') return (
    <div className="ta2"><style>{style}</style>
      <Breadcrumb />
      <div className="ta2-header">
        <div>
          <div className="ta2-title">{activeSubject}</div>
          <div className="ta2-sub">{activeClass?.name} · Section {activeSection}</div>
        </div>
        <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
          <button className="btn btn-blue" onClick={()=>setNavStep('create')}><PlusIcon />Add Assessment</button>
        </div>
      </div>
      <div className="panel">
        <div className="panel-head">
          <div className="panel-title">Assessments ({assessments.length})</div>
        </div>
        {loading ? (
          <div className="ta2-loading"><div className="spinner"/>Loading…</div>
        ) : assessments.length === 0 ? (
          <div className="ta2-empty">
            No assessments for {activeSubject} yet.<br/>
            <button className="btn btn-blue btn-sm" style={{marginTop:12}} onClick={()=>setNavStep('create')}>
              <PlusIcon />Add First Assessment
            </button>
          </div>
        ) : assessments.map(a => (
          <div key={a._id} className="row-item" onClick={()=>openMarks(a)}>
            <div style={{flex:1,minWidth:0}}>
              <div style={{display:'flex',alignItems:'center',gap:8,flexWrap:'wrap'}}>
                <div className="row-title">{a.assessmentType?.name}</div>
                <span className={`status-badge ${a.status}`}>{a.status.replace('_',' ')}</span>
                {/* ── OBE: CLO chips in list ── */}
                {obeEnabled && a.cloMappings?.length > 0 && <>
                  {a.cloMappings.slice(0,3).map((m,i) => (
                    <span key={i} className="clo-chip">{m.clo?.code||'CLO'}</span>
                  ))}
                  {a.cloMappings.length > 3 && <span className="clo-chip">+{a.cloMappings.length-3}</span>}
                </>}
                {a.fileUrl && (
                  <span style={{display:'inline-flex',alignItems:'center',gap:3,fontSize:'.68rem',color:'#3b6ef8',fontWeight:600}}>
                    <PaperClipIcon style={{width:12,height:12,strokeWidth:2.5}} />File
                  </span>
                )}
              </div>
              <div className="row-sub">{fmtDate(a.date)} · Max: {a.maxMarks}</div>
              {a.description && <div className="row-desc">"{a.description}"</div>}
              {a.status==='unlock_requested' && <div style={{fontSize:'.7rem',color:'#f59e0b',marginTop:3}}>⏳ Awaiting admin unlock approval</div>}
              {a.status==='unlocked'         && <div style={{fontSize:'.7rem',color:'#a855f7',marginTop:3}}>🔓 Unlocked — you can edit marks</div>}
            </div>
            <div className="row-actions" onClick={e=>e.stopPropagation()}>
              <button className="btn btn-ghost btn-sm" onClick={()=>openMarks(a)}><PencilSquareIcon />Marks</button>
              {a.status==='draft' && (
                <button className="btn btn-red btn-sm" onClick={e=>handleDelete(a._id,e)}><TrashIcon /></button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  // ── SECTIONS VIEW ────────────────────────────────────────────────────────────
  if (navStep === 'sections' && activeClass) {
    const secs = activeClass.sections || [];
    return (
      <div className="ta2"><style>{style}</style>
        <Breadcrumb />
        <div className="ta2-header">
          <div><div className="ta2-title">{activeClass.name}</div><div className="ta2-sub">Select a section</div></div>
        </div>
        {secs.length === 0 ? <div className="ta2-empty">No sections found.</div> : (
          <div className="card-grid">
            {secs.map((sec,i) => (
              <div key={sec} className="grade-card" onClick={()=>goToSubjects(sec)}>
                <div style={{width:36,height:36,borderRadius:10,background:`${SUBJ_COLORS[i%SUBJ_COLORS.length]}18`,color:SUBJ_COLORS[i%SUBJ_COLORS.length],display:'flex',alignItems:'center',justifyContent:'center',fontWeight:700,fontSize:'.9rem',marginBottom:8}}>
                  {sec}
                </div>
                <div className="gc-name">Section {sec}</div>
                <div className="gc-sub">{teacherSubjects.length} subject{teacherSubjects.length!==1?'s':''}</div>
                <div className="gc-arrow">
                  <span style={{fontSize:'.72rem',color:SUBJ_COLORS[i%SUBJ_COLORS.length],fontWeight:600}}>View subjects</span>
                  <ChevronRightIcon style={{width:15,height:15,stroke:SUBJ_COLORS[i%SUBJ_COLORS.length]}} />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // ── SUBJECTS VIEW ────────────────────────────────────────────────────────────
  if (navStep === 'subjects' && activeClass) {
    const subjects = getSubjects();
    return (
      <div className="ta2"><style>{style}</style>
        <Breadcrumb />
        <div className="ta2-header">
          <div><div className="ta2-title">Section {activeSection}</div><div className="ta2-sub">{activeClass.name} · Select a subject</div></div>
        </div>
        {subjects.length === 0 ? <div className="ta2-empty">No subjects assigned.</div> : (
          <div className="card-grid">
            {subjects.map((subj,i) => (
              <div key={subj} className="subj-card" onClick={()=>goToList(subj)}>
                <div style={{width:32,height:32,borderRadius:9,background:`${SUBJ_COLORS[i%SUBJ_COLORS.length]}18`,color:SUBJ_COLORS[i%SUBJ_COLORS.length],display:'flex',alignItems:'center',justifyContent:'center',fontWeight:700,fontSize:'.72rem',marginBottom:8}}>
                  {subj.slice(0,2).toUpperCase()}
                </div>
                <div className="gc-name">{subj}</div>
                <div className="gc-arrow">
                  <span style={{fontSize:'.72rem',color:SUBJ_COLORS[i%SUBJ_COLORS.length],fontWeight:600}}>View assessments</span>
                  <ChevronRightIcon style={{width:15,height:15,stroke:SUBJ_COLORS[i%SUBJ_COLORS.length]}} />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // ── GRADE CARDS (root) ───────────────────────────────────────────────────────
  return (
    <div className="ta2"><style>{style}</style>
      <div className="ta2-header">
        <div><div className="ta2-title">Assessments</div><div className="ta2-sub">Select a class to manage assessments</div></div>
      </div>
      {loading ? (
        <div className="ta2-loading"><div className="spinner"/>Loading…</div>
      ) : classes.length === 0 ? (
        <div className="ta2-empty">No classes assigned to you yet.</div>
      ) : (
        <div className="card-grid">
          {classes.map((cls,i) => (
            <div key={cls._id} className="grade-card" onClick={()=>goToSections(cls)}>
              <div style={{width:36,height:36,borderRadius:10,background:`${SUBJ_COLORS[i%SUBJ_COLORS.length]}18`,color:SUBJ_COLORS[i%SUBJ_COLORS.length],display:'flex',alignItems:'center',justifyContent:'center',fontWeight:700,fontSize:'.8rem',marginBottom:8}}>
                {cls.name?.replace('Grade ','G').slice(0,3)}
              </div>
              <div className="gc-name">{cls.name}</div>
              <div className="gc-sub">{cls.sections?.length||0} section{cls.sections?.length!==1?'s':''}</div>
              {teacherSubjects.length > 0 && (
                <div style={{display:'flex',gap:4,flexWrap:'wrap',marginTop:6}}>
                  {teacherSubjects.map(s => (
                    <span key={s} style={{fontSize:'.65rem',fontWeight:600,padding:'2px 7px',borderRadius:99,background:'#f3f0ff',color:'#8b5cf6'}}>{s}</span>
                  ))}
                </div>
              )}
              <div className="gc-arrow">
                <span style={{fontSize:'.72rem',color:'#3b6ef8',fontWeight:600}}>View subjects</span>
                <ChevronRightIcon style={{width:15,height:15,stroke:'#3b6ef8'}} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default TeacherAssessments;